import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Clock, CalendarDays, ListChecks, Megaphone, Briefcase, ChevronRight, Loader2, CheckCircle2 } from "lucide-react";
import empApi from "../../lib/empApi";
import { useEmployeeAuth } from "../../context/EmployeeAuthContext";
import { roleLabel } from "./empRoles";

const STATUS_TONE = {
  "Belum Check-in": "bg-amber-500/15 text-amber-300",
  "Sedang Bekerja": "bg-emerald-500/15 text-emerald-300",
  "Selesai": "bg-slate-600/30 text-slate-300",
};

// Workflow quick actions per blueprint: Absensi · Jobdesk · Pengajuan · Pengumuman.
const ACTIONS = [
  { to: "/karyawan/absensi", label: "Absensi", icon: Clock },
  { to: "/karyawan/jobdesk", label: "Jobdesk", icon: Briefcase },
  { to: "/karyawan/jadwal", label: "Pengajuan", icon: CalendarDays },
  { to: "/karyawan/pengumuman", label: "Pengumuman", icon: Megaphone },
];

const strip = (html) => (html || "").replace(/<[^>]+>/g, " ").replace(/&nbsp;/g, " ").trim();

function Card({ children, className = "", ...rest }) {
  return <div className={`rounded-2xl border border-slate-800 bg-slate-800/40 p-4 ${className}`} {...rest}>{children}</div>;
}

export default function EmpDashboard() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  const [anns, setAnns] = useState([]);
  const navigate = useNavigate();
  const { employee } = useEmployeeAuth();

  useEffect(() => {
    empApi.get("/employee/dashboard").then(({ data }) => setD(data)).catch(() => setErr(true));
    empApi.get("/employee/announcements").then(({ data }) => setAnns((data.announcements || []).slice(0, 3))).catch(() => {});
  }, []);

  if (err) return <p className="py-10 text-center text-sm text-slate-500" data-testid="emp-dashboard-error">Gagal memuat beranda. Coba muat ulang.</p>;
  if (!d) return <div className="flex h-64 items-center justify-center"><Loader2 className="animate-spin text-amber-400" /></div>;

  const tasks = d.pending_tasks || [];
  const shift = d.today_shift && d.today_shift.type === "work" ? d.today_shift : null;

  return (
    <div className="space-y-3.5" data-testid="emp-dashboard">
      {/* Greeting */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-slate-400">Selamat datang,</p>
          <h2 className="font-heading text-xl font-bold text-white">{d.employee.full_name}</h2>
        </div>
        <span className="rounded-full bg-amber-400/15 px-3 py-1 text-xs font-bold text-amber-300" data-testid="emp-dash-role">{roleLabel(employee?.position)}</span>
      </div>

      {/* Attendance Status */}
      <Card data-testid="emp-dash-attendance">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-slate-300"><Clock size={18} /> <span className="text-sm font-semibold">Status Absensi</span></div>
          <span className={`rounded-full px-3 py-1 text-xs font-bold ${STATUS_TONE[d.attendance_status] || "bg-slate-700 text-slate-300"}`} data-testid="emp-dash-att-status">{d.attendance_status}</span>
        </div>
        <button onClick={() => navigate("/karyawan/absensi")} data-testid="emp-dash-goto-attendance"
          className="mt-3 w-full rounded-xl bg-gradient-to-r from-amber-400 to-orange-500 py-2.5 text-sm font-bold text-slate-900 active:scale-[0.99]">
          {d.attendance_status === "Belum Check-in" ? "Check In Sekarang" : d.attendance_status === "Sedang Bekerja" ? "Lanjut: Jobdesk / Check Out" : "Lihat Absensi"}
        </button>
      </Card>

      {/* Today's Shift + Today's Tasks */}
      <div className="grid grid-cols-2 gap-3">
        <Card data-testid="emp-dash-shift">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-300"><CalendarDays size={14} /> Shift Hari Ini</div>
          <p className="mt-2 font-heading text-lg font-bold text-white" data-testid="emp-dash-shift-value">
            {shift ? `${shift.shift_start}–${shift.shift_end}` : (d.today_shift?.type === "off" ? "Libur" : d.today_shift?.type === "holiday" ? "Hari Libur" : "—")}
          </p>
          <button onClick={() => navigate("/karyawan/jadwal")} className="mt-1 text-[11px] font-semibold text-amber-400">Lihat jadwal ›</button>
        </Card>

        <button onClick={() => navigate("/karyawan/checklist")} className="rounded-2xl border border-slate-800 bg-slate-800/40 p-4 text-left active:scale-[0.99]" data-testid="emp-dash-tasks">
          <div className="flex items-center justify-between text-xs font-semibold text-slate-300">
            <span className="flex items-center gap-1.5"><ListChecks size={14} /> Tugas Hari Ini</span>
            <ChevronRight size={14} className="text-amber-400" />
          </div>
          <p className="mt-2 font-heading text-lg font-bold text-white" data-testid="emp-dash-tasks-count">{tasks.length} tugas</p>
          <p className="mt-0.5 truncate text-[11px] text-slate-400">{tasks[0] || "Tidak ada tugas"}</p>
        </button>
      </div>

      {/* Quick Actions */}
      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Aksi Cepat</p>
        <div className="grid grid-cols-4 gap-2" data-testid="emp-quick-actions">
          {ACTIONS.map((q) => (
            <button key={q.to} onClick={() => navigate(q.to)} data-testid={`emp-quick-${q.label.toLowerCase()}`}
              className="flex flex-col items-center gap-1.5 rounded-2xl border border-slate-800 bg-slate-800/40 py-3 text-slate-300 active:scale-95">
              <q.icon size={20} className="text-amber-400" />
              <span className="text-[11px] font-semibold">{q.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Announcements (HR) */}
      <Card data-testid="emp-dash-announcements">
        <div className="mb-2 flex items-center justify-between">
          <div className="flex items-center gap-2 text-slate-300"><Megaphone size={18} /> <span className="text-sm font-semibold">Pengumuman</span></div>
          <button onClick={() => navigate("/karyawan/pengumuman")} className="text-[11px] font-semibold text-amber-400" data-testid="emp-dash-ann-all">Lihat semua ›</button>
        </div>
        {anns.length === 0 ? (
          <p className="py-2 text-xs text-slate-500" data-testid="emp-dash-ann-empty">Belum ada pengumuman.</p>
        ) : (
          <ul className="space-y-2.5" data-testid="emp-dash-ann-list">
            {anns.map((n) => (
              <li key={n.id} className="flex items-start gap-2 cursor-pointer" onClick={() => navigate("/karyawan/pengumuman")}>
                <CheckCircle2 size={14} className={`mt-0.5 shrink-0 ${n.read ? "text-slate-600" : "text-amber-400"}`} />
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-white">{n.title}</p>
                  <p className="line-clamp-2 text-xs text-slate-400">{strip(n.body_html)}</p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
